import { useEffect, useState } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import {
  solarPosition,
  lunarPosition,
  planetPositions,
  moonPhaseFromFraction,
  type PlanetPosition,
} from "@/lib/astronomy";
import { useLocation } from "@/components/location-provider";
import { cn } from "@/lib/utils";

interface SkyDomeProps {
  // Optional: take cloud cover and weather icon for atmospheric tinting
  cloudPct?: number;
  description?: string;
}

// Map azimuth (0=N, 90=E, 180=S, 270=W) and altitude (0..90) onto an SVG dome.
// The dome is a half-circle; we show the sky as if looking up from below.
// To reduce confusion, we orient it: bottom of card is horizon, top is zenith,
// horizontal axis: N (left) → E → S (center) → W → N (right). For a north-
// hemisphere observer the sun is to the south, which lands center-bottom area
// at solar noon.

function projectToScreen(
  altitudeDeg: number,
  azimuthDeg: number,
  width: number,
  height: number,
): { x: number; y: number } {
  // Project unit vector onto screen.
  // We want: az=180 (south) at center, az=90 (east) at left, az=270 (west) at right
  // Actually, to feel "natural" we'll mirror so east is left.
  // But conventional: looking south, east is on left. Let's do:
  //   az=180 → x = width/2
  //   az=90  → x = width
  //   az=270 → x = 0
  const cx = width / 2;
  const horizonY = height - 8;
  // Use stereographic-ish: r grows with cos(altitude)
  const r = (Math.cos(altitudeDeg * (Math.PI / 180)) * (width / 2 - 20));
  // Azimuth angle relative to south
  const azFromSouth = (azimuthDeg - 180) * (Math.PI / 180);
  // Mirror: positive azFromSouth means west of south → right side of view
  const x = cx + r * Math.sin(azFromSouth);
  // y: altitude → up
  const y = horizonY - Math.sin(altitudeDeg * (Math.PI / 180)) * (height - 24);
  return { x, y };
}

function genStars(count: number): { x: number; y: number; size: number; opacity: number }[] {
  // Deterministic pseudo-random stars based on simple seeded LCG so they're
  // stable across renders.
  let seed = 1337;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0xffffffff;
  };
  const stars = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      x: rand() * 800,
      y: rand() * 280,
      size: rand() * 1.6 + 0.4,
      opacity: rand() * 0.6 + 0.3,
    });
  }
  return stars;
}

const STARS = genStars(140);

export function SkyDome({ cloudPct = 50, description }: SkyDomeProps) {
  const [now, setNow] = useState(() => new Date());
  const { current: location } = useLocation();

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(interval);
  }, []);

  const sun = solarPosition(now, location.lat, location.lon);
  const moon = lunarPosition(now, location.lat, location.lon);
  const planets = planetPositions(now, location.lat, location.lon);

  const isDay = sun.altitudeDeg > -0.833;
  const dawn = sun.altitudeDeg > -6 && sun.altitudeDeg < 0;
  const twilight = sun.altitudeDeg <= -0.833 && sun.altitudeDeg > -18;

  // Sky tint / gradient
  const skyTopColor = isDay
    ? "oklch(0.55 0.18 240)"
    : dawn
      ? "oklch(0.4 0.18 320)"
      : twilight
        ? "oklch(0.18 0.1 270)"
        : "oklch(0.07 0.05 270)";
  const skyBottomColor = isDay
    ? "oklch(0.85 0.1 230)"
    : dawn
      ? "oklch(0.65 0.18 50)"
      : twilight
        ? "oklch(0.3 0.15 50)"
        : "oklch(0.1 0.04 270)";

  const W = 800;
  const H = 280;

  const sunPos = projectToScreen(Math.max(-15, sun.altitudeDeg), sun.azimuthDeg, W, H);
  const moonPos = projectToScreen(Math.max(-15, moon.altitudeDeg), moon.azimuthDeg, W, H);
  const moonPhase = moonPhaseFromFraction(moon.phaseAngleDeg / 360);

  const visiblePlanets = planets.filter((p) => p.altitudeDeg > -5);

  // Sun arc (today's path): show altitude vs azimuth across the day at 30-min steps.
  const arcPoints: { x: number; y: number; below: boolean }[] = [];
  for (let h = 0; h < 24; h += 0.5) {
    const t = new Date(now);
    t.setHours(h, 0, 0, 0);
    const sp = solarPosition(t, location.lat, location.lon);
    const proj = projectToScreen(Math.max(-15, sp.altitudeDeg), sp.azimuthDeg, W, H);
    arcPoints.push({ x: proj.x, y: proj.y, below: sp.altitudeDeg < -0.833 });
  }

  // Show stars only when sky is darker
  const showStars = !isDay;
  const starOpacity = isDay ? 0 : twilight ? 0.4 : 1;

  return (
    <GlassPanel
      intensity="medium"
      className="relative overflow-hidden p-0 border-white/10"
    >
      <div className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto block"
          style={{ background: `linear-gradient(to bottom, ${skyTopColor}, ${skyBottomColor})` }}
          preserveAspectRatio="xMidYMid slice"
        >
          {/* Atmospheric grid */}
          <defs>
            <radialGradient id="sun-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="oklch(0.95 0.18 85)" stopOpacity="0.8" />
              <stop offset="40%" stopColor="oklch(0.85 0.2 75)" stopOpacity="0.4" />
              <stop offset="100%" stopColor="oklch(0.85 0.2 75)" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="moon-glow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="oklch(0.95 0.02 250)" stopOpacity="0.5" />
              <stop offset="100%" stopColor="oklch(0.85 0.02 250)" stopOpacity="0" />
            </radialGradient>
            <linearGradient id="horizon-haze" x1="0" x2="0" y1="1" y2="0">
              <stop offset="0%" stopColor="oklch(0.06 0.03 250)" stopOpacity="0.45" />
              <stop offset="100%" stopColor="oklch(0.06 0.03 250)" stopOpacity="0" />
            </linearGradient>
          </defs>

          {/* Stars (night) */}
          {showStars &&
            STARS.map((s, i) => (
              <circle
                key={i}
                cx={s.x}
                cy={s.y}
                r={s.size}
                fill="white"
                opacity={s.opacity * starOpacity}
              />
            ))}

          {/* Sun arc — dotted line across today's path */}
          {(() => {
            const path = arcPoints
              .map((p, i) => `${i === 0 ? "M" : "L"} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`)
              .join(" ");
            return (
              <path
                d={path}
                stroke={isDay ? "oklch(0.95 0.1 85 / 0.35)" : "oklch(0.7 0.15 240 / 0.3)"}
                strokeWidth="1.5"
                strokeDasharray="2 4"
                fill="none"
              />
            );
          })()}

          {/* Cardinal direction labels along horizon */}
          {[
            { az: 0, label: "N" },
            { az: 90, label: "E" },
            { az: 180, label: "S" },
            { az: 270, label: "W" },
          ].map(({ az, label }) => {
            const p = projectToScreen(0, az, W, H);
            return (
              <g key={label}>
                <line
                  x1={p.x}
                  y1={H - 8}
                  x2={p.x}
                  y2={H - 4}
                  stroke="white"
                  strokeOpacity="0.6"
                />
                <text
                  x={p.x}
                  y={H - 12}
                  textAnchor="middle"
                  className="text-[10px] fill-white/85 font-bold tracking-wider"
                >
                  {label}
                </text>
              </g>
            );
          })}

          {/* Horizon haze + line */}
          <rect x="0" y={H - 64} width={W} height={64} fill="url(#horizon-haze)" />
          <line
            x1="0"
            y1={H - 8}
            x2={W}
            y2={H - 8}
            stroke="white"
            strokeOpacity="0.4"
            strokeDasharray="2 2"
          />

          {/* Visible planets */}
          {visiblePlanets.map((p) => {
            const pos = projectToScreen(p.altitudeDeg, p.azimuthDeg, W, H);
            const planetColors: Record<string, string> = {
              Mercury: "oklch(0.85 0.05 80)",
              Venus: "oklch(0.95 0.05 90)",
              Mars: "oklch(0.7 0.18 30)",
              Jupiter: "oklch(0.85 0.1 80)",
              Saturn: "oklch(0.85 0.08 70)",
            };
            const color = planetColors[p.name] ?? "white";
            const r = p.name === "Venus" || p.name === "Jupiter" ? 4 : 2.8;
            return (
              <g key={p.name}>
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={r * 2.5}
                  fill={color}
                  opacity="0.15"
                />
                <circle cx={pos.x} cy={pos.y} r={r} fill={color} />
                <text
                  x={pos.x + 8}
                  y={pos.y + 3}
                  className="text-[9px] fill-white/80 font-semibold"
                >
                  {p.name}
                </text>
              </g>
            );
          })}

          {/* Moon */}
          {moon.altitudeDeg > -8 && (
            <g>
              <circle
                cx={moonPos.x}
                cy={moonPos.y}
                r={42}
                fill="url(#moon-glow)"
              />
              {/* Render the moon disk with phase shadow */}
              <MoonDisk
                cx={moonPos.x}
                cy={moonPos.y}
                r={12}
                phaseFraction={moon.phaseAngleDeg / 360}
              />
              <text
                x={moonPos.x + 16}
                y={moonPos.y + 4}
                className="text-[10px] fill-white/85 font-bold"
              >
                Moon
              </text>
            </g>
          )}

          {/* Sun */}
          {sun.altitudeDeg > -10 && (
            <g>
              <circle
                cx={sunPos.x}
                cy={sunPos.y}
                r={70}
                fill="url(#sun-glow)"
                className="glow-pulse"
              />
              <circle cx={sunPos.x} cy={sunPos.y} r={14} fill="oklch(0.95 0.18 85)" />
              <text
                x={sunPos.x + 18}
                y={sunPos.y + 4}
                className="text-[10px] fill-white/85 font-bold"
              >
                Sun
              </text>
            </g>
          )}

          {/* Cloud overlay (fades sky if heavy clouds) */}
          {cloudPct > 30 && (
            <rect
              x="0"
              y="0"
              width={W}
              height={H}
              fill="oklch(0.6 0.02 240)"
              opacity={Math.min(0.45, (cloudPct / 100) * 0.5)}
            />
          )}
        </svg>

        {/* Header overlay */}
        <div className="absolute top-3 left-4 right-4 flex items-start justify-between gap-3 pointer-events-none">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/85 font-semibold">
              Sky Now
            </p>
            <h2 className="text-base sm:text-lg font-bold text-white tracking-tight drop-shadow">
              {location.name}
              {location.country ? `, ${location.country}` : ""}
            </h2>
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/85 font-semibold">
              Local time
            </p>
            <p className="text-base font-bold text-white tabular-nums tracking-tight drop-shadow">
              {now.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
            </p>
          </div>
        </div>

        {/* Bottom legend */}
        <div className="absolute bottom-3 left-4 right-4 flex flex-wrap items-end justify-between gap-2 pointer-events-none">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-white/85">
            <span>
              <span className="text-white/60">Sun</span>{" "}
              <span className="font-semibold tabular-nums text-white">
                {sun.altitudeDeg.toFixed(0)}° · az {sun.azimuthDeg.toFixed(0)}°
              </span>
            </span>
            <span>
              <span className="text-white/60">Moon</span>{" "}
              <span className="font-semibold tabular-nums text-white">
                {moon.altitudeDeg.toFixed(0)}° · {Math.round(moon.illumination * 100)}%
              </span>
            </span>
            <span className="inline-flex items-center gap-1">
              <span aria-hidden>{moonPhase.emoji}</span>
              <span className="text-white">{moonPhase.name}</span>
            </span>
          </div>
          <span className="text-[10px] text-white/60 capitalize">
            {description ? `${description} · ` : ""}
            {cloudPct}% cloud
          </span>
        </div>
      </div>
    </GlassPanel>
  );
}

// Moon disk with phase shading. phaseFraction: 0=new, 0.5=full, 1=new
function MoonDisk({
  cx,
  cy,
  r,
  phaseFraction,
}: {
  cx: number;
  cy: number;
  r: number;
  phaseFraction: number;
}) {
  // Lit fraction
  const phase = phaseFraction; // 0..1
  // Use SVG arcs to shade unlit portion. The terminator is an ellipse.
  // Lit hemisphere: right side when waxing (0..0.5), left side when waning (0.5..1)
  const waxing = phase < 0.5;
  const illum = 1 - Math.abs(phase * 2 - 1); // 0..1 (waxing/waning)

  // Render: full lit circle, then overlay shadow path on the unlit side.
  // Simplify by drawing two halves with a vertical terminator ellipse.
  // We'll approximate with two SVG paths.

  const lightColor = "oklch(0.95 0.02 250)";
  const shadowColor = "oklch(0.18 0.02 250)";

  // For the terminator ellipse, the horizontal radius is r * |1 - 2*illum|
  const termRx = r * Math.abs(1 - 2 * illum);

  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={shadowColor} />
      {/* Lit half */}
      <path
        d={
          waxing
            ? `M ${cx} ${cy - r} A ${r} ${r} 0 0 1 ${cx} ${cy + r} A ${termRx} ${r} 0 0 ${illum < 0.5 ? 0 : 1} ${cx} ${cy - r} Z`
            : `M ${cx} ${cy - r} A ${r} ${r} 0 0 0 ${cx} ${cy + r} A ${termRx} ${r} 0 0 ${illum < 0.5 ? 1 : 0} ${cx} ${cy - r} Z`
        }
        fill={lightColor}
      />
    </g>
  );
}
